import { useEffect, useRef } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router'
import { closeSnackbar } from 'notistack'
import BarChart from '@mui/icons-material/BarChart'
import Settings from '@mui/icons-material/Settings'
import {
  AppBar,
  IconButton,
  Link,
  Toolbar,
  Stack,
  useTheme,
  Tooltip,
  Button,
  Badge,
} from '@mui/material'
import Logo from '@/assets/Logo'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useGetOrgCredentials } from '@/service-library/hooks/org-credentials'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { showWarningSnackbar } from '@/utils/snackbars'
import AccountPicker from '@/components/accounts/AccountPicker'
import BreadcrumbPortal from '@/components/breadcrumbs/BreadcrumbPortal'
import NotificationsMenu from '@/components/notifications/NotificationsMenu'
import TopBarPortal from '@/components/top-bar-portal/TopBarPortal'
import HelpMenu from './HelpMenu'
import ProfileMenu from './ProfileMenu'
import SuperUserMenu from './SuperUserMenu'

export default function AppTopBar() {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const isSuperUser = useIsSuperUser()
  const featureFlags = useFeatureFlagContext()
  const navigate = useNavigate()
  const initialLoadRef = useRef(true)

  const [dismissCheckCredential, setDismissCheckCredential] = useLocalStorage(
    'dismiss-check-credentials-warning',
    false,
  )

  const { orgCredentials, isPending } = useGetOrgCredentials({
    filters: {
      limit: '1',
      is_authenticated: 'false',
    },
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (!initialLoadRef.current) return
    if (!dismissCheckCredential && !isPending && orgCredentials?.length > 0) {
      initialLoadRef.current = false

      showWarningSnackbar('Some credentials need to be authenticated.', {
        autoHideDuration: 5000,
        preventDuplicate: true,
        action: (
          <Stack direction="row" spacing={1}>
            <Button
              color="inherit"
              variant="text"
              size="small"
              onClick={() => {
                navigate(`/settings/integrations/${orgCredentials[0].type_id}`)
              }}
            >
              Go to Credentials
            </Button>
            <Button
              color="inherit"
              variant="text"
              size="small"
              onClick={() => {
                closeSnackbar()
                setDismissCheckCredential(true)
              }}
            >
              Dismiss
            </Button>
          </Stack>
        ),
      })
    }
  }, [
    dismissCheckCredential,
    isPending,
    navigate,
    orgCredentials,
    setDismissCheckCredential,
  ])

  return (
    <AppBar
      position="static"
      elevation={isDark ? 1 : 0}
      sx={{
        zIndex: theme.zIndex.drawer + 2,
        borderBottom: isDark ? 'none' : `1px solid ${theme.palette.divider}`,
        background: isDark ? undefined : theme.palette.background.paper,
      }}
    >
      <Toolbar
        component={Stack}
        direction="row"
        disableGutters
        justifyContent="space-between"
        variant="dense"
        sx={{ py: '2px' }}
      >
        <Stack direction="row" alignItems="flex-end">
          <Link ml={1} mb={2.5} to="/projects" component={RouterLink}>
            <Logo />
          </Link>
          <div style={{ alignSelf: 'center' }}>
            <BreadcrumbPortal />
          </div>
        </Stack>
        <Stack direction="row" alignItems="center">
          <AccountPicker />
          <Tooltip title="Document Metrics">
            <IconButton
              component={RouterLink}
              to="/metrics/documents"
              size="small"
            >
              <BarChart />
            </IconButton>
          </Tooltip>
          <Tooltip title="Global Settings">
            <Badge
              overlap="circular"
              variant="dot"
              color="warning"
              invisible={!orgCredentials.length}
            >
              <IconButton component={RouterLink} to="/settings" size="small">
                <Settings />
              </IconButton>
            </Badge>
          </Tooltip>
          <HelpMenu />
          {featureFlags.show_notifications && <NotificationsMenu />}
          <ProfileMenu />
          {isSuperUser && <SuperUserMenu />}
        </Stack>
      </Toolbar>
      <TopBarPortal />
      <TopBarPortal isPrimary={false} />
    </AppBar>
  )
}
